import React, { useState, useEffect } from 'react';
import { MapPin, Package, ArrowRight, ArrowDown, Navigation, Loader2, Power } from 'lucide-react';
import { listenToOpenDeliveries, claimDelivery } from './deliveryService';
import { getDistanceFromLatLonInKm } from './utils';

const SIZE_LABEL = { small: 'קטן', medium: 'בינוני', large: 'גדול' };

export default function CourierJobList({ currentUser, courierLocation, isOnline, onToggleOnline, onClaimed, onClose }) {
  const [deliveries, setDeliveries] = useState([]);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    const unsub = listenToOpenDeliveries(setDeliveries);
    return () => unsub();
  }, []);

  // מיון לפי קירבה לנקודת האיסוף
  const sorted = [...deliveries]
    .map((d) => {
      let dist = null;
      if (courierLocation && d.pickup?.lat) {
        dist = getDistanceFromLatLonInKm(courierLocation.lat, courierLocation.lng, d.pickup.lat, d.pickup.lng);
      }
      return { ...d, distance: dist };
    })
    .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999));

  const handleClaim = async (delivery) => {
    setClaiming(delivery.id);
    try {
      await claimDelivery(delivery.id, currentUser);
      onClaimed(delivery.id);
    } catch (e) {
      alert(e.message); // "המשלוח כבר נתפס על ידי שליח אחר"
      setClaiming(null);
    }
  };

  return (
    <div className="left-sidebar-list pointer-events-auto font-sans" dir="rtl">

      {/* Header */}
      <div className="bg-white p-4 shadow-sm flex items-center gap-3 sticky top-0 z-10 border-b border-gray-100">
        <button onClick={onClose} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
          <ArrowRight size={20} />
        </button>
        <div className="flex-1">
          <h2 className="font-bold text-lg">משלוחים זמינים</h2>
          <p className="text-xs text-gray-500">{courierLocation ? 'ממוין לפי קירבה' : 'כל המשלוחים'}</p>
        </div>
        <div className="text-xs font-bold bg-black text-white px-2 py-1 rounded-md">{sorted.length}</div>
      </div>

      {/* מתג מקוון/לא מקוון */}
      <div className="p-3 bg-white border-b border-gray-100">
        <button
          onClick={onToggleOnline}
          className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
            isOnline ? 'bg-green-500 text-white shadow-lg shadow-green-200' : 'bg-gray-100 text-gray-500'
          }`}
        >
          <Power size={18} />
          {isOnline ? 'מקוון — מקבל משלוחים' : 'לא מקוון — לחץ כדי להתחיל'}
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
        {!isOnline ? (
          <div className="text-center text-gray-400 mt-10 px-6">
            <Power size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">עבור למצב "מקוון" כדי לראות משלוחים ולקבל אותם.</p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center text-gray-400 mt-10">
            <Package size={32} className="mx-auto mb-3 opacity-30" />
            <p>אין כרגע משלוחים פתוחים באזור.</p>
            <p className="text-xs mt-1">העדכון מיידי — משלוח חדש יופיע כאן אוטומטית.</p>
          </div>
        ) : (
          sorted.map((d) => (
            <div key={d.id} className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
              <div className="absolute right-0 top-0 bottom-0 w-1 bg-blue-500"></div>

              <div className="pr-3">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-black text-blue-700 text-lg">₪{d.fare}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-bold">
                      {SIZE_LABEL[d.parcel?.size] || 'קטן'}
                    </span>
                    {d.distance !== null && (
                      <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <MapPin size={8} fill="currentColor" /> {d.distance.toFixed(1)} ק"מ
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-sm text-gray-700 font-medium mb-2 line-clamp-1">{d.parcel?.description}</p>

                {/* מסלול */}
                <div className="space-y-1 mb-3 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 shrink-0"></span>
                    <span className="text-gray-600 truncate">{d.pickup?.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowDown size={10} className="text-gray-300 mr-0" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-sm bg-red-500 shrink-0"></span>
                    <span className="text-gray-600 truncate">{d.dropoff?.address}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-50">
                  <span className="text-[11px] text-gray-400">{d.distanceKm} ק"מ נסיעה · ~{d.durationMin} דק'</span>
                  <button
                    onClick={() => handleClaim(d)}
                    disabled={claiming === d.id}
                    className="bg-black text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1 active:scale-95 transition-transform disabled:opacity-50"
                  >
                    {claiming === d.id ? <Loader2 size={14} className="animate-spin" /> : <><Navigation size={14} /> קח משלוח</>}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
        <div className="h-10"></div>
      </div>
    </div>
  );
}
